import { useState, useRef, useCallback, useEffect } from 'react';

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'recorded' | 'error';

export interface UseAudioRecorderResult {
  state: RecorderState;
  audioFile: File | null;
  durationMs: number;
  amplitudes: number[];
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  reset: () => void;
}

const MAX_DURATION_MS = 120_000; // 2 minutes max
const AMPLITUDE_SAMPLE_INTERVAL_MS = 50;

export function useAudioRecorder(): UseAudioRecorderResult {
  const [state, setState] = useState<RecorderState>('idle');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [amplitudes, setAmplitudes] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const startTimeRef = useRef<number>(0);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const amplitudeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    if (amplitudeTimerRef.current) clearInterval(amplitudeTimerRef.current);
    if (maxDurationTimerRef.current) clearTimeout(maxDurationTimerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    analyserRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startRecording = useCallback(async () => {
    setError(null);
    setState('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState('error');
      setError('Microphone access denied. Please allow microphone access and try again.');
      return;
    }

    streamRef.current = stream;

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.addEventListener('dataavailable', (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    });

    recorder.addEventListener('stop', () => {
      cleanup();
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const file = new File([blob], `recording-${Date.now()}.webm`, { type: mimeType });
      setAudioFile(file);
      setState('recorded');
    });

    recorder.start(100);
    startTimeRef.current = Date.now();
    setState('recording');

    durationTimerRef.current = setInterval(() => {
      setDurationMs(Date.now() - startTimeRef.current);
    }, 100);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    amplitudeTimerRef.current = setInterval(() => {
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const normalized = Math.min(1, avg / 128);
      setAmplitudes((prev) => [...prev.slice(-60), normalized]);
    }, AMPLITUDE_SAMPLE_INTERVAL_MS);

    maxDurationTimerRef.current = setTimeout(() => {
      recorder.stop();
    }, MAX_DURATION_MS);
  }, [cleanup]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setState('idle');
    setAudioFile(null);
    setDurationMs(0);
    setAmplitudes([]);
    setError(null);
    chunksRef.current = [];
  }, [cleanup]);

  return { state, audioFile, durationMs, amplitudes, error, startRecording, stopRecording, reset };
}
